import { useEffect, useState, memo } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { FiMenu, FiSearch } from 'react-icons/fi';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
import ContentWrapper from '../ContentWrapper';
import Button from '../Button';
import LanguageDropdown from '../LanguageDropdown/LanguageDropdown';
import useIsAboveBreakpoint from '../../../core/hooks/useIsAboveBreakpoint';
import Toaster from '../Toaster/Toaster';
import Link from '../Link/Link';
import { VisitStandardWebsiteButton } from '../VisitStandardWebsiteButton';
import { useSelector } from '../../../core/redux/store';
import UserProfile from '../Icons/UserProfile';
import SkipTranslationWrapper from '../SkipTranslationWrapper';
import tailwindConfig from '../../../../tailwind.config';

const NavigationHotelSearchBar = dynamic(() => import('../NavigationHotelSearchBar'));
const UserDropdown = dynamic(() => import('../../account/UserDropdown'));

type Props = {
  hasShadow: boolean;
  showSidebar: boolean;
  onShowSidebar: (value: boolean) => void;
  showSearchBar?: boolean;
  showBookingConfirmation?: boolean;
  showAccountMenu?: boolean;
  isNavBarSticky?: boolean;
};

const NavBar = ({
  hasShadow,
  showSidebar,
  onShowSidebar,
  showSearchBar = false,
  showBookingConfirmation = true,
  showAccountMenu = true,
  isNavBarSticky = false,
}: Props) => {
  const router = useRouter();
  const isTopPage = router.pathname === '/';
  const shadow = hasShadow && !isTopPage ? 'shadow-md z-10 relative' : '';
  const isInLoginPage = router.pathname === '/account/login';
  const { userAttributes, userInitialized } = useSelector((state) => state.account);
  const { family_name: familyName, given_name: givenName, accountId, loggedIn } = userAttributes;
  const isLoggedIn = Boolean(accountId);

  const [showSearch, setShowSearch] = useState(false);

  const isAboveMdBreakpoint = useIsAboveBreakpoint('MD');

  useEffect(() => {
    if (isAboveMdBreakpoint) {
      onShowSidebar(false);
    }
  }, [isAboveMdBreakpoint, onShowSidebar]);

  const { t } = useTranslation(['common']);
  return (
    <>
      <nav
        className={classNames(`${shadow} transition-all`, {
          'bg-white': isNavBarSticky || !isTopPage,
          'bg-transparent': isTopPage && !isNavBarSticky,
        })}
      >
        <ContentWrapper>
          <div className="relative hidden size-full justify-center lg:flex">
            <VisitStandardWebsiteButton className="absolute top-2" />
          </div>

          <div className="flex h-14 justify-between sm:h-16">
            {/* Desktop */}
            <div className="hidden w-auto items-center gap-6 sm:flex">
              <Link href="/" passHref prefetch={false}>
                <button type="button" className="flex">
                  <Image
                    src="/logo_square.svg"
                    alt="Tabist Logo"
                    width={56}
                    height={56}
                    className="cursor-pointer"
                  />
                </button>
              </Link>

              <div className="hidden w-auto items-center sm:flex lg:hidden">
                <VisitStandardWebsiteButton className="absolute top-2" />
              </div>
            </div>

            {/* Mobile */}
            <div className="flex w-auto items-center sm:hidden">
              <Link href="/" passHref prefetch={false}>
                <button type="button" className="flex">
                  <Image
                    src="/logo_square.svg"
                    alt="Tabist Logo"
                    width={40}
                    height={40}
                    className="cursor-pointer"
                  />
                </button>
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex">
                {showSearchBar && (
                  <div
                    className="flex items-center"
                    onClick={() => setShowSearch(!showSearch)}
                    onKeyDown={() => setShowSearch(!showSearch)}
                    role="button"
                    aria-label="Search"
                    tabIndex={0}
                  >
                    <div className="rounded-full p-3 hover:bg-gray-50">
                      <FiSearch />
                    </div>
                  </div>
                )}
                <div className="hidden md:block">
                  <LanguageDropdown />
                </div>
              </div>

              {userInitialized && (
                <>
                  {isAboveMdBreakpoint && showBookingConfirmation && !isLoggedIn && (
                    <Button
                      className="h-10 p-4 text-sm font-normal hover:bg-gray-50 hover:opacity-100"
                      to="/guest/booking-id"
                      textColor="red"
                      color="white"
                      rounded
                    >
                      {t('common:button.booking_confirmation')}
                    </Button>
                  )}
                  {/* Desktop */}
                  {showAccountMenu && loggedIn && familyName && isAboveMdBreakpoint && (
                    <UserDropdown />
                  )}
                  {/* Mobile */}
                  {showAccountMenu && loggedIn && familyName && !isAboveMdBreakpoint && (
                    <div
                      onClick={() => onShowSidebar(!showSidebar)}
                      onKeyDown={() => onShowSidebar(!showSidebar)}
                      role="button"
                      aria-label="Sidebar Menu"
                      tabIndex={0}
                      className="flex h-10 max-w-[135px] items-center gap-1 rounded-4xl border border-gray-200 bg-white py-2 pl-4 pr-2 text-gray-600"
                    >
                      <SkipTranslationWrapper tag="p" className="w-full truncate">
                        {familyName} {givenName}
                      </SkipTranslationWrapper>
                      <UserProfile color="gray" size={40} />
                    </div>
                  )}
                  {showAccountMenu && !loggedIn && (
                    <>
                      {/* Desktop */}
                      <div className="hidden items-center sm:flex">
                        <Button
                          rounded
                          className="h-10 p-4 text-[13px] font-bold hover:bg-orange-800 hover:opacity-100"
                          to={
                            // Don't redirect user back to /account related page such as (sign up, forgot password) if they are anonymous user previously
                            router?.asPath?.indexOf('/account') !== 0
                              ? `/account/login?${new URLSearchParams({
                                  redirect: router.asPath || '',
                                }).toString()}`
                              : '/account/login'
                          }
                          size="small"
                          color="green"
                        >
                          {t('common:button.login')} / {t('common:button.register_short')}
                        </Button>
                      </div>

                      {/* Mobile */}
                      {!isInLoginPage && (
                        <div className="flex items-center sm:hidden">
                          <Button
                            rounded
                            className="h-[34px] w-[139px] p-0 text-[13px] font-bold hover:bg-orange-800 hover:opacity-100"
                            to={
                              // Don't redirect user back to /account related page such as (sign up, forgot password) if they are anonymous user previously
                              router?.asPath?.indexOf('/account') !== 0
                                ? `/account/login?${new URLSearchParams({
                                    redirect: router.asPath || '',
                                  }).toString()}`
                                : '/account/login'
                            }
                            size="small"
                            color="green"
                          >
                            {t('common:button.login')} / {t('common:button.register_short')}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                  {!isAboveMdBreakpoint && !loggedIn && (
                    <div
                      onClick={() => onShowSidebar(!showSidebar)}
                      onKeyDown={() => onShowSidebar(!showSidebar)}
                      role="button"
                      aria-label="Sidebar Menu"
                      tabIndex={0}
                    >
                      <FiMenu
                        color={
                          isNavBarSticky || router.pathname !== '/'
                            ? tailwindConfig.theme.colors.gray[100]
                            : 'white'
                        }
                        size={32}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          {showSearch && showSearchBar && (
            <NavigationHotelSearchBar isAboveMdBreakpoint={isAboveMdBreakpoint} />
          )}
        </ContentWrapper>
      </nav>

      <Toaster />
    </>
  );
};

export default memo(NavBar);
